import { JwtPayload, jwtDecode } from "jwt-decode";

// LOCAL stays enabled by default; other environments require an explicit opt-in.
const isLocalDebug =
  import.meta.env.DEV &&
  String(import.meta.env.VITE_REACT_APP_URL_BASE_WEB ?? "").startsWith(
    "http://localhost",
  );
const isSessionDebugEnabled =
  isLocalDebug ||
  String(import.meta.env.VITE_REACT_APP_EXP_LOG ?? "").toLowerCase() === "true";

/** Formatea una duración en ms a texto legible (p. ej. "1h 5m", "4m 30s", "850ms"). */
export const formatDuration = (ms: number | null): string => {
  if (ms === null) return "n/a";
  const sign = ms < 0 ? "-" : "";
  const abs = Math.abs(ms);
  if (abs < 1000) return `${sign}${Math.round(abs)}ms`;

  const totalSeconds = Math.round(abs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [
    days && `${days}d`,
    hours && `${hours}h`,
    minutes && `${minutes}m`,
    seconds && `${seconds}s`,
  ].filter(Boolean);
  return `${sign}${parts.join(" ") || "0s"}`;
};

/** Traza de diagnóstico de sesión, habilitada en LOCAL o mediante EXP_LOG=true. */
export const sessionDebugLog = (message: string, data?: unknown) => {
  if (!isSessionDebugEnabled) return;
  const time = new Date().toLocaleTimeString();
  const visibility = document.visibilityState;
  if (data === undefined) console.info(`[session ${time} ${visibility}] ${message}`);
  else console.info(`[session ${time} ${visibility}] ${message}`, data);
};

/** Decodifica un JWT almacenado y calcula los segundos restantes hasta su expiración. */
export const getJwtRemainingSeconds = (
  storedToken: string | null,
  nowMs = Date.now(),
): number | null => {
  if (!storedToken) return null;

  try {
    const token: unknown = JSON.parse(storedToken);
    if (typeof token !== "string") return null;

    const { exp } = jwtDecode<JwtPayload>(token);
    return exp ? exp - Math.floor(nowMs / 1000) : null;
  } catch {
    return null;
  }
};

/** Datos de emisión/expiración del JWT almacenado, solo para diagnóstico. */
export const describeStoredJwt = (storedToken: string | null) => {
  try {
    const token: unknown = storedToken ? JSON.parse(storedToken) : null;
    if (typeof token !== "string") return null;

    const { iat, exp, tfp } = jwtDecode<JwtPayload & { tfp?: string }>(token);
    return {
      policy: tfp,
      issuedAt: iat ? new Date(iat * 1000).toLocaleString() : null,
      expiresAt: exp ? new Date(exp * 1000).toLocaleString() : null,
      lifetime: iat && exp ? formatDuration((exp - iat) * 1000) : null,
    };
  } catch {
    return null;
  }
};